import { useState, useEffect } from 'react';
import { Panel, Flex, Input, Button, CellList, CellSimple, Typography } from '@maxhub/max-ui';
import { useHousesStore } from '../store/houses.store.ts';
import { createMeter, addMember, removeMember } from '../api/houses.ts';
import { getAdminUsers } from '../api/admin.ts';

const METER_LABEL: Record<string, string> = {
  cold: '🔵 Холодная вода',
  hot: '🔴 Горячая вода',
};

interface ManageHousePageProps {
  houseId: string;
  onBack: () => void;
}

export function ManageHousePage({ houseId, onBack }: ManageHousePageProps) {
  const { houses, meters, loadMeters } = useHousesStore();

  const house = houses.find((h) => h.id === houseId) ?? null;
  const houseMeters = meters[houseId] ?? [];

  // ─── Add meter ───────────────────────────────────────────────────────────
  const [showAddMeter, setShowAddMeter] = useState(false);
  const [meterType, setMeterType] = useState<'cold' | 'hot'>('cold');
  const [meterSerial, setMeterSerial] = useState('');
  const [meterLocation, setMeterLocation] = useState('');
  const [addingMeter, setAddingMeter] = useState(false);
  const [meterError, setMeterError] = useState<string | null>(null);

  // ─── Add member ──────────────────────────────────────────────────────────
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberPhone, setMemberPhone] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);

  useEffect(() => {
    loadMeters(houseId);
  }, [houseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddMeter = async () => {
    setAddingMeter(true);
    setMeterError(null);
    try {
      await createMeter(houseId, {
        type: meterType,
        serialNumber: meterSerial || undefined,
        location: meterLocation || undefined,
      });
      setShowAddMeter(false);
      setMeterSerial('');
      setMeterLocation('');
      await loadMeters(houseId);
    } catch (err: unknown) {
      setMeterError(await extractErrorMessage(err));
    } finally {
      setAddingMeter(false);
    }
  };

  const handleAddMember = async () => {
    const phone = memberPhone.trim();
    if (!phone) {
      setMemberError('Введите номер телефона');
      return;
    }
    setAddingMember(true);
    setMemberError(null);
    try {
      // Ищем пользователя по телефону через admin API
      const userId = await resolveUserIdByPhone(phone);
      await addMember(houseId, userId);
      setShowAddMember(false);
      setMemberPhone('');
    } catch (err: unknown) {
      setMemberError(await extractErrorMessage(err));
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await removeMember(houseId, userId);
      useHousesStore.getState().loadHouses();
    } catch {
      // ignore
    }
  };

  if (!house) {
    return (
      <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
        <Button mode="link" onClick={onBack}>← Назад</Button>
        <Typography.Label style={{ display: 'block', marginTop: '1rem' }}>Дом не найден</Typography.Label>
      </Panel>
    );
  }

  const members = house.members ?? [];

  return (
    <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
      <Button mode="link" onClick={onBack} style={{ marginBottom: '1rem' }}>
        ← Назад
      </Button>

      <Typography.Headline variant="medium-strong" style={{ display: 'block', marginBottom: '1.25rem' }}>
        {house.fullAddress ?? `Дом ${house.buildingNumber}`}
      </Typography.Headline>

      {/* ─── Счётчики ───────────────────────────────────────────────────── */}
      <section style={{ marginBottom: '1.5rem' }}>
        <Flex justify="space-between" align="center" style={{ marginBottom: '0.75rem' }}>
          <Typography.Headline variant="small-strong">Счётчики</Typography.Headline>
          <Button
            mode="secondary"
            size="small"
            onClick={() => setShowAddMeter(!showAddMeter)}
          >
            {showAddMeter ? 'Отмена' : '+ Добавить'}
          </Button>
        </Flex>

        {showAddMeter && (
          <Panel
            mode="secondary"
            style={{ borderRadius: 10, padding: '0.75rem', marginBottom: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          >
            <div>
              <Typography.Label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                Тип
              </Typography.Label>
              <select
                style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: 8, fontSize: '0.9rem', background: '#fff', width: '100%' }}
                value={meterType}
                onChange={(e) => setMeterType(e.target.value as 'cold' | 'hot')}
              >
                <option value="cold">Холодная вода</option>
                <option value="hot">Горячая вода</option>
              </select>
            </div>
            <div>
              <Typography.Label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                Серийный номер
              </Typography.Label>
              <Input
                mode="primary"
                placeholder="Необязательно"
                value={meterSerial}
                onChange={(e) => setMeterSerial(e.target.value)}
              />
            </div>
            <div>
              <Typography.Label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                Расположение
              </Typography.Label>
              <Input
                mode="primary"
                placeholder="Например: кухня"
                value={meterLocation}
                onChange={(e) => setMeterLocation(e.target.value)}
              />
            </div>
            {meterError && (
              <Typography.Label style={{ display: 'block', color: '#c62828' }}>
                {meterError}
              </Typography.Label>
            )}
            <Button
              mode="primary"
              stretched
              loading={addingMeter}
              disabled={addingMeter}
              onClick={handleAddMeter}
            >
              Добавить счётчик
            </Button>
          </Panel>
        )}

        {houseMeters.length === 0 ? (
          <Typography.Label style={{ display: 'block', opacity: 0.5, margin: '0.5rem 0' }}>
            Счётчики не добавлены
          </Typography.Label>
        ) : (
          <CellList mode="island">
            {houseMeters.map((m) => (
              <CellSimple
                key={m.id}
                title={METER_LABEL[m.type] ?? m.type}
                subtitle={[m.serialNumber ? `№ ${m.serialNumber}` : null, m.location].filter(Boolean).join(' · ') || undefined}
              />
            ))}
          </CellList>
        )}
      </section>

      {/* ─── Совладельцы ────────────────────────────────────────────────── */}
      <section style={{ marginBottom: '1.5rem' }}>
        <Flex justify="space-between" align="center" style={{ marginBottom: '0.75rem' }}>
          <Typography.Headline variant="small-strong">Совладельцы</Typography.Headline>
          <Button
            mode="secondary"
            size="small"
            onClick={() => setShowAddMember(!showAddMember)}
          >
            {showAddMember ? 'Отмена' : '+ Добавить'}
          </Button>
        </Flex>

        {showAddMember && (
          <Panel
            mode="secondary"
            style={{ borderRadius: 10, padding: '0.75rem', marginBottom: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          >
            <div>
              <Typography.Label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                Номер телефона
              </Typography.Label>
              <Input
                mode="primary"
                placeholder="+7 999 123-45-67"
                type="tel"
                value={memberPhone}
                onChange={(e) => { setMemberPhone(e.target.value); setMemberError(null); }}
              />
            </div>
            {memberError && (
              <Typography.Label style={{ display: 'block', color: '#c62828' }}>
                {memberError}
              </Typography.Label>
            )}
            <Button
              mode="primary"
              stretched
              loading={addingMember}
              disabled={addingMember}
              onClick={handleAddMember}
            >
              Добавить совладельца
            </Button>
          </Panel>
        )}

        {members.filter((m) => m.role === 'co_owner').length === 0 ? (
          <Typography.Label style={{ display: 'block', opacity: 0.5, margin: '0.5rem 0' }}>
            Совладельцев нет
          </Typography.Label>
        ) : (
          <CellList mode="island">
            {members
              .filter((m) => m.role === 'co_owner')
              .map((m) => (
                <CellSimple
                  key={m.id}
                  title={m.user?.fullName ?? m.user?.phone ?? m.userId}
                  after={
                    <Button
                      mode="secondary"
                      size="small"
                      appearance="negative"
                      onClick={() => handleRemoveMember(m.userId)}
                    >
                      Удалить
                    </Button>
                  }
                />
              ))}
          </CellList>
        )}
      </section>
    </Panel>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Нормализует номер: убирает всё кроме цифр, приводит 8xxx → 7xxx */
function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('8')) {
    return '7' + digits.slice(1);
  }
  return digits;
}

/**
 * Ищет пользователя по номеру телефона через admin API.
 * Требует роли admin. Если пользователь не найден — бросает Error.
 */
async function resolveUserIdByPhone(rawPhone: string): Promise<string> {
  const normalized = normalizePhone(rawPhone);
  let users;
  try {
    users = await getAdminUsers();
  } catch {
    throw new Error('Поиск по телефону доступен только администратору');
  }
  const found = users.find((u) => u.phone && normalizePhone(u.phone) === normalized);
  if (!found) {
    throw new Error('Пользователь с таким номером не найден');
  }
  return found.id;
}

/** Переводы стандартных backend-ошибок */
const ERROR_TRANSLATIONS: Record<string, string> = {
  'Already a member': 'Пользователь уже является участником этого дома',
  'User not found': 'Пользователь не найден',
  'House not found': 'Дом не найден',
  'Forbidden': 'Нет прав для этого действия',
  'Cannot remove yourself': 'Нельзя удалить себя из дома',
};

async function extractErrorMessage(err: unknown): Promise<string> {
  // Проверяем .response (ky HTTPError) раньше instanceof Error,
  // иначе HTTPError перехватится как Error и мы потеряем JSON body
  if (err && typeof err === 'object' && 'response' in err) {
    try {
      const res = (err as { response: Response }).response;
      const body = await res.clone().json();
      const raw: string = body?.error ?? '';
      return ERROR_TRANSLATIONS[raw] ?? (raw || 'Ошибка');
    } catch {
      // игнорируем — body уже прочитан или не JSON
    }
  }
  if (err instanceof Error) return err.message;
  return 'Ошибка';
}
